import { AxiosInstance } from 'axios';
import { FeedbackCreateRequest } from '../types/chat.types';

/** Response from POST /session */
export interface SessionResponse {
  session_id: string;
  content: unknown;
}

/** Response from POST /message */
export interface MessageResponse {
  message: string;
  trace_id: string;
}

/**
 * Unified API interface for all agents.
 * Every agent exposes the same 4 core operations + feedback.
 */
export interface AgentApi {
  createSession(): Promise<SessionResponse>;
  sendMessage(sessionId: string, message: string): Promise<MessageResponse>;
  getSession(sessionId: string): Promise<SessionResponse>;
  deleteSession(sessionId: string): Promise<void>;
  submitFeedback(
    sessionId: string,
    data: FeedbackCreateRequest,
  ): Promise<void>;
}

/**
 * Creates an AgentApi implementation backed by an Axios client.
 * The Axios client should already have auth interceptors configured.
 */
export function createAgentApi(client: AxiosInstance): AgentApi {
  return {
    async createSession(): Promise<SessionResponse> {
      const response = await client.post<SessionResponse>(
        '/api/chatbot/sessions',
      );
      return response.data;
    },

    async sendMessage(
      sessionId: string,
      message: string,
    ): Promise<MessageResponse> {
      const response = await client.post<MessageResponse>(
        `/api/chatbot/sessions/${sessionId}/messages`,
        message,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response.data;
    },

    async getSession(sessionId: string): Promise<SessionResponse> {
      const response = await client.get<SessionResponse>(
        `/api/chatbot/sessions/${sessionId}`,
      );
      return response.data;
    },

    async deleteSession(sessionId: string): Promise<void> {
      await client.delete(`/api/chatbot/sessions/${sessionId}`);
    },

    async submitFeedback(
      sessionId: string,
      data: FeedbackCreateRequest,
    ): Promise<void> {
      await client.post(
        `/api/chatbot/sessions/${sessionId}/feedback`,
        data,
      );
    },
  };
}
